import useAuth from "../../hooks/useAuth";
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import UndoIcon from "@mui/icons-material/Undo";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import TodayIcon from "@mui/icons-material/Today";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import BoltIcon from "@mui/icons-material/Bolt";
import ShoppingBagIcon from "@mui/icons-material/ShoppingBag";
import InventoryIcon from "@mui/icons-material/Inventory";
import RestaurantMenuIcon from "@mui/icons-material/RestaurantMenu";
import FastfoodIcon from "@mui/icons-material/Fastfood";
import LocalDrinkIcon from "@mui/icons-material/LocalDrink";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import ViewListIcon from "@mui/icons-material/ViewList";
import EventNoteIcon from "@mui/icons-material/EventNote";
import Swal from "sweetalert2";
import {
  dailyAvailabilityApi,
  DailyAvailabilityResponse,
  DailyMealRecord,
  DailyComboRecord,
  MenuSideItem,
} from "../../api/dailyAvailability";
import { nowMealsApi } from "../../api/nowMeals";
import { itemCategoriesApi } from "../../api/itemCategories";
import kitchensApi from "../../api/kitchens";
import { getDailyMealStatus } from "../../utils/dailyMealStatus";
import DailyMealCards from "../../components/kinnow/DailyMealCards";
import DailySharedPools from "../../components/kinnow/DailySharedPools";
import InstantDayPlanDialog from "../../components/kinnow/InstantDayPlanDialog";

// Helper to get formatted YYYY-MM-DD for current local IST date
const getTodayISTString = () => {
  return new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });
};

const addDaysToDate = (dateStr: string, days: number) => {
  const d = new Date(`${dateStr}T12:00:00Z`);
  d.setDate(d.getDate() + days);
  return d.toISOString().split("T")[0];
};

const DailyAvailabilityTab: React.FC = () => {
  const { admin } = useAuth();
  const canEditSides = admin?.permissions?.nowMeals ?? ["admin", "super-admin", "manager"].includes(admin?.role || "");
  const [savingSide, setSavingSide] = useState(false);
  const todayStr = getTodayISTString();
  const [selectedDate, setSelectedDate] = useState<string>(todayStr);
  const [mealView, setMealView] = useState<"cards" | "list">("cards");
  const [planOpen, setPlanOpen] = useState(false);
  const [kitchens, setKitchens] = useState<Array<{ _id: string; name: string }>>([]);
  const [selectedKitchenId, setSelectedKitchenId] = useState("");
  const [kitchenError, setKitchenError] = useState("");
  const requestId = useRef(0);
  const loadedScope = useRef("");
  const currentScope = useRef("");
  currentScope.current = `${selectedDate}:${selectedKitchenId}`;
  const [data, setData] = useState<DailyAvailabilityResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [refreshing, setRefreshing] = useState(false);
  const [generating, setGenerating] = useState<boolean>(false);
  const [search, setSearch] = useState<string>("");
  const [categoryFilter, setCategoryFilter] = useState<string>("");
  const [statusFilter, setStatusFilter] = useState<"ALL" | "LIVE" | "UNAVAILABLE">("ALL");
  const [comboStatusFilter, setComboStatusFilter] = useState<"ALL" | "LIVE" | "OFF_SCHEDULE">("ALL");
  const [categories, setCategories] = useState<Array<{ _id: string; name: string; displayOrder?: number }>>([]);

  // All catalog meals (for Add Extra Meal modal)
  const [allCatalogMeals, setAllCatalogMeals] = useState<any[]>([]);

  // Dialog states
  const [qtyModalOpen, setQtyModalOpen] = useState(false);
  const [editStockMode, setEditStockMode] = useState<"LIMITED" | "UNLIMITED">("LIMITED");
  const [savingQuantity, setSavingQuantity] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<DailyMealRecord | null>(null);
  const [newQuantity, setNewQuantity] = useState<number | string>(20);
  const [overrideReason, setOverrideReason] = useState<string>("");

  const [addMealModalOpen, setAddMealModalOpen] = useState(false);
  const [addMealId, setAddMealId] = useState<string>("");
  const [addMealQty, setAddMealQty] = useState<number>(20);
  const [addMealStockMode, setAddMealStockMode] = useState<"LIMITED" | "UNLIMITED">("LIMITED");
  const [addMealReason, setAddMealReason] = useState<string>("");

  const fetchCategoriesAndMeals = async () => {
    try {
      const [catRes, mealsRes] = await Promise.all([
        itemCategoriesApi.getCategories(),
        nowMealsApi.getNowMeals({ limit: 1000, isActive: true }),
      ]);
      setCategories(catRes.categories || []);
      setAllCatalogMeals(mealsRes.nowMeals || []);
    } catch (err) {
      console.error("Error fetching categories or catalog meals", err);
    }
  };

  const loadDailyAvailability = useCallback(async (date: string, preserveView = true) => {
    const scope = `${date}:${selectedKitchenId}`;
    if (!selectedKitchenId || currentScope.current !== scope) return;
    const id = ++requestId.current;
    const keepView = preserveView && loadedScope.current === scope;
    try {
      setLoading(!keepView);
      setRefreshing(keepView);
      if (!keepView) {
        loadedScope.current = "";
        setData(null);
      }
      const res = await dailyAvailabilityApi.getDaily(date, selectedKitchenId);
      if (requestId.current === id && currentScope.current === scope) {
        loadedScope.current = scope;
        setData(res);
      }
      return res;
    } catch (err) {
      console.error("Failed to load daily availability", err);
      if (requestId.current === id && currentScope.current === scope) Swal.fire("Error", "Could not load daily availability for date", "error");
    } finally {
      if (requestId.current === id && currentScope.current === scope) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  }, [selectedKitchenId]);

  useEffect(() => {
    let cancelled = false;
    kitchensApi.getAccessibleKitchens().then(response => {
      if (cancelled) return;
      const available = response.kitchens || [];
      setKitchens(available);
      if (available.length) setSelectedKitchenId(available[0]._id);
      else {
        setKitchenError("No accessible kitchens");
        setLoading(false);
      }
    }).catch(() => {
      if (!cancelled) {
        setKitchenError("Could not load kitchens");
        setLoading(false);
      }
    });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    void fetchCategoriesAndMeals();
  }, []);

  useEffect(() => {
    setQtyModalOpen(false);
    setSelectedRecord(null);
    setAddMealModalOpen(false);
    setPlanOpen(false);
    void loadDailyAvailability(selectedDate, false);
  }, [selectedDate, loadDailyAvailability]);

  const handleDateChange = (newDate: string) => {
    setSelectedDate(newDate);
  };

  const handleGenerate = async (forceReset = false) => {
    if (!selectedKitchenId) return;
    const date = selectedDate;
    const kitchenId = selectedKitchenId;
    if (!forceReset && (data?.summary.manualOverrideCount || 0) > 0) {
      const choice = await Swal.fire({
        title: "Recalculate availability?",
        text: "Apply the latest rules. Keep daily overrides, or reset them to rule defaults? Sold quantities are preserved either way.",
        showDenyButton: true,
        showCancelButton: true,
        confirmButtonText: "Keep overrides",
        denyButtonText: "Reset overrides",
      });
      if (!choice.isConfirmed && !choice.isDenied) return;
      forceReset = choice.isDenied;
    }
    if (currentScope.current !== `${date}:${kitchenId}`) return;
    try {
      setGenerating(true);
      const res = await dailyAvailabilityApi.generate(date, forceReset, kitchenId);
      const refreshed = await loadDailyAvailability(date);
      if (currentScope.current !== `${date}:${kitchenId}`) return;
      if (!refreshed) {
        Swal.fire("Refresh failed", "Recalculation finished, but the updated menu could not be loaded. Refresh to verify the counts.", "warning");
        return;
      }
      const scheduled = refreshed?.records.filter(record => getDailyMealStatus(record) !== "OFF_SCHEDULE") || [];
      const inStock = scheduled.filter(record => getDailyMealStatus(record) === "LIVE").length;
      const failed = res.errorsCount > 0 || res.success === false;
      Swal.fire({
        title: failed ? "Recalculation incomplete" : "Availability recalculated",
        text: `${date}: ${scheduled.length} scheduled meals, ${inStock} in stock, ${scheduled.length - inStock} out of stock. ${res.overridesPreserved || 0} daily overrides kept.${failed ? ` ${res.errorsCount || 0} items failed to update.` : ""}`,
        icon: failed ? "warning" : "success",
      });
    } catch (err: any) {
      console.error("Generation error", err);
      Swal.fire("Error", err.response?.data?.message || "Failed to generate availability", "error");
    } finally {
      setGenerating(false);
    }
  };

  const handleToggle = async (record: DailyMealRecord) => {
    if (record.isProjectedOnly) {
      Swal.fire(
        "Generate First",
        "This future date is currently an estimated preview. Click 'Lock In / Generate' to customize.",
        "info"
      );
      return;
    }

    try {
      await dailyAvailabilityApi.toggleAvailability(
        record._id,
        !record.isAvailable,
        !record.isAvailable ? "Manual enable" : "Manual disable"
      );
      await loadDailyAvailability(selectedDate);
    } catch (err) {
      console.error("Toggle error", err);
      Swal.fire("Error", "Failed to update availability status", "error");
    }
  };

  const handleOpenQtyModal = (record: DailyMealRecord) => {
    if (record.isProjectedOnly) {
      Swal.fire(
        "Generate First",
        "Please click 'Lock In / Generate' first before customizing quantities for this date.",
        "info"
      );
      return;
    }
    setSelectedRecord(record);
    setEditStockMode(record.stockMode);
    setNewQuantity(record.remainingQuantity ?? 0);
    setOverrideReason(record.overrideReason || "");
    setQtyModalOpen(true);
  };

  const handleSaveQuantity = async () => {
    if (!selectedRecord || savingQuantity) return;
    if (editStockMode === "LIMITED" && (newQuantity === "" || !Number.isInteger(Number(newQuantity)) || Number(newQuantity) < 0)) {
      Swal.fire("Invalid quantity", "Enter a non-negative whole number", "warning");
      return;
    }
    setSavingQuantity(true);
    try {
      await dailyAvailabilityApi.adjustQuantity(
        selectedRecord._id,
        editStockMode === "UNLIMITED" ? 0 : Number(newQuantity),
        overrideReason,
        editStockMode,
        "REMAINING"
      );
      setQtyModalOpen(false);
      await loadDailyAvailability(selectedDate);
    } catch (err) {
      console.error("Save quantity error", err);
      Swal.fire("Error", "Failed to update quantity", "error");
    } finally {
      setSavingQuantity(false);
    }
  };

  const handleRevert = async (record: DailyMealRecord) => {
    const result = await Swal.fire({
      title: "Revert to Rule Default?",
      text: "This will clear any manual overrides and restore capacity derived from recurring rules.",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Revert",
    });

    if (result.isConfirmed) {
      try {
        await dailyAvailabilityApi.revertOverride(record._id);
        await loadDailyAvailability(selectedDate);
      } catch (err) {
        console.error("Revert error", err);
        Swal.fire("Error", "Failed to revert override", "error");
      }
    }
  };

  const handleAddManualMeal = async () => {
    if (!addMealId || !selectedKitchenId) {
      Swal.fire("Required", "Please select a meal", "warning");
      return;
    }

    try {
      await dailyAvailabilityApi.addManualMeal({
        date: selectedDate,
        mealId: addMealId,
        quantity: addMealQty,
        stockMode: addMealStockMode,
        reason: addMealReason,
        kitchenId: selectedKitchenId,
      });
      setAddMealModalOpen(false);
      setAddMealId("");
      setAddMealQty(20);
      setAddMealReason("");
      await loadDailyAvailability(selectedDate);
    } catch (err) {
      console.error("Add meal error", err);
      Swal.fire("Error", "Failed to add meal to date", "error");
    }
  };

  const [activeSubTab, setActiveSubTab] = useState<"meals" | "combos" | "sides">("meals");

  const handleToggleCombo = async (comboRecord: DailyComboRecord) => {
    if (comboRecord.isProjectedOnly) return;
    try {
      const nextStatus = !comboRecord.isAvailable;
      const targetId = comboRecord._id?.startsWith("proj_") ? comboRecord.mealId._id : comboRecord._id;
      await dailyAvailabilityApi.toggleComboAvailability(
        targetId,
        nextStatus,
        undefined,
        selectedDate
      );
      await loadDailyAvailability(selectedDate);
    } catch (err) {
      console.error("Toggle combo error", err);
      Swal.fire("Error", "Failed to update combo availability", "error");
    }
  };

  const handleToggleSide = async (side: MenuSideItem) => {
    if (!canEditSides || savingSide) return;
    const confirmation = await Swal.fire({ title: "Change shared side availability?", text: `${side.name} will be ${side.available ? "unavailable" : "available"} across all kitchens.`, icon: "warning", showCancelButton: true, confirmButtonText: "Confirm" });
    if (!confirmation.isConfirmed) return;
    setSavingSide(true);
    try {
      const nextStatus = !side.available;
      await dailyAvailabilityApi.toggleSideAvailability(side._id, nextStatus, selectedKitchenId);
      await loadDailyAvailability(selectedDate);
    } catch (err) {
      console.error("Toggle side error", err);
      Swal.fire("Error", "Failed to update side availability", "error");
    } finally { setSavingSide(false); }
  };

  // Filter records
  const totalMealRecords = (data?.records || []).filter(record => getDailyMealStatus(record) !== "OFF_SCHEDULE");
  const liveMealsCount = totalMealRecords.filter((r) => getDailyMealStatus(r) === "LIVE").length;
  const unavailableMealsCount = totalMealRecords.length - liveMealsCount;

  const filteredRecords = totalMealRecords.filter((r) => {
    const nameMatch = r.mealId?.name.toLowerCase().includes(search.toLowerCase());
    const catMatch = categoryFilter
      ? String(r.mealId?.categoryId) === categoryFilter ||
        String(r.mealId?.categoryId?._id) === categoryFilter
      : true;
    const statusMatch =
      statusFilter === "ALL"
        ? true
        : statusFilter === "LIVE"
        ? getDailyMealStatus(r) === "LIVE"
        : ["SOLD_OUT", "PAUSED"].includes(getDailyMealStatus(r));
    return nameMatch && catMatch && statusMatch;
  });

  const totalComboRecords = data?.combos || [];
  const liveCombosCount = totalComboRecords.filter((c) => c.isAvailable).length;
  const offScheduleCombosCount = totalComboRecords.filter((c) => !c.isAvailable).length;

  const filteredCombos = totalComboRecords.filter((c) => {
    if (comboStatusFilter === "LIVE") return Boolean(c.isAvailable);
    if (comboStatusFilter === "OFF_SCHEDULE") return !c.isAvailable;
    return true;
  });

  const isToday = selectedDate === todayStr;
  const isFuture = selectedDate > todayStr;
  const isProjected = data?.isProjected || false;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: { xs: 2, sm: 2.5 }, minWidth: 0,
      "& .MuiButton-root": { borderRadius: 2, boxShadow: "none" },
      "& .MuiOutlinedInput-root": { borderRadius: 2 },
    }}>
      {kitchenError && <Alert severity="error">{kitchenError}</Alert>}
      {/* Date Navigation & Actions Bar */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 1.5, sm: 2 },
          borderRadius: 3,
          bgcolor: "background.paper",
          border: "1px solid",
          borderColor: "divider",
          boxShadow: "0 2px 8px rgba(15,23,42,0.025)",
        }}
      >
        <Stack
          direction={{ xs: "column", xl: "row" }}
          sx={{ flexWrap: "wrap", gap: 2 }}
          alignItems={{ xs: "stretch", xl: "center" }}
          justifyContent="space-between"
        >
          <Stack direction={{ xs: "column", md: "row" }} sx={{ gap: 2, minWidth: 0 }} alignItems={{ xs: "stretch", md: "center" }}>
          <FormControl size="small" sx={{ minWidth: { xs: 0, md: 200 }, width: { xs: "100%", md: "auto" }, maxWidth: { md: 260 } }}>
            <InputLabel id="daily-kitchen-label">Kitchen</InputLabel>
            <Select labelId="daily-kitchen-label" label="Kitchen" value={selectedKitchenId} disabled={generating || kitchens.length === 0}
              onChange={(event) => setSelectedKitchenId(event.target.value)}>
              {kitchens.map(kitchen => <MenuItem key={kitchen._id} value={kitchen._id}>{kitchen.name}</MenuItem>)}
            </Select>
          </FormControl>
          {/* Date Navigator */}
          <Stack direction="row" alignItems="center" sx={{
            display: { xs: "grid", sm: "flex" },
            gridTemplateColumns: "40px minmax(0, 1fr) minmax(0, 1fr) 40px",
            flexWrap: "wrap", gap: 1, minWidth: 0,
            "& .MuiButton-root": { minWidth: 0, minHeight: 40 },
            "& .MuiChip-root": { gridColumn: "1 / -1", justifySelf: "start", maxWidth: "100%", letterSpacing: 0 },
          }}>
            <IconButton
              size="small"
              aria-label="Previous day"
              onClick={() => handleDateChange(addDaysToDate(selectedDate, -1))}
              sx={{ border: "1px solid", borderColor: "divider", width: 40, height: 40 }}
            >
              <ArrowBackIosNewIcon fontSize="small" />
            </IconButton>

            <Button
              variant={isToday ? "contained" : "outlined"}
              size="small"
              onClick={() => handleDateChange(todayStr)}
              startIcon={<TodayIcon />}
              sx={{ textTransform: "none", fontWeight: 600 }}
            >
              Today
            </Button>

            <Button
              variant={selectedDate === addDaysToDate(todayStr, 1) ? "contained" : "outlined"}
              size="small"
              onClick={() => handleDateChange(addDaysToDate(todayStr, 1))}
              sx={{ textTransform: "none", fontWeight: 600 }}
            >
              Tomorrow
            </Button>

            <TextField
              type="date"
              size="small"
              value={selectedDate}
              onChange={(e) => handleDateChange(e.target.value)}
              inputProps={{ "aria-label": "Menu date" }}
              sx={{ width: { xs: "100%", sm: 170 }, minWidth: 0, gridColumn: "1 / -1", gridRow: 2,
                "& input": { minWidth: 0 } }}
            />

            <IconButton
              size="small"
              aria-label="Next day"
              onClick={() => handleDateChange(addDaysToDate(selectedDate, 1))}
              sx={{ border: "1px solid", borderColor: "divider", width: 40, height: 40, gridColumn: 4, gridRow: 1 }}
            >
              <ArrowForwardIosIcon fontSize="small" />
            </IconButton>

            {/* Status Pill */}
            {isToday ? (
              <Chip
                label="Live today · IST"
                color="success"
                size="small"
                icon={<BoltIcon />}
                sx={{ fontWeight: 600, bgcolor: "#edf7ef", color: "success.dark", "& .MuiChip-icon": { color: "success.main" } }}
              />
            ) : isProjected ? (
              <Chip
                label="ESTIMATED (RULE PROJECTION)"
                color="secondary"
                size="small"
                icon={<WarningAmberIcon />}
                sx={{ fontWeight: 700, letterSpacing: 0.5 }}
              />
            ) : (
              <Chip
                label={data?.status === "MODIFIED" ? "CUSTOMIZED / OVERRIDDEN" : "GENERATED"}
                color="primary"
                size="small"
                icon={<CheckCircleIcon />}
                sx={{ fontWeight: 700, letterSpacing: 0.5 }}
              />
            )}
          </Stack>
          </Stack>

          {/* Action Buttons */}
          <Stack direction="row" alignItems="center" sx={{
            display: { xs: "grid", sm: "flex" }, gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
            flexWrap: "wrap", gap: 1, minWidth: 0,
            "& .MuiButton-root": { minHeight: 40, minWidth: 0, whiteSpace: "normal", textTransform: "none" },
          }}>
            <Button variant="text" color="inherit" startIcon={<EventNoteIcon />} onClick={() => setPlanOpen(true)} disabled={loading || generating || !selectedKitchenId} sx={{ color: "text.secondary" }}>Day Plan</Button>
            <Tooltip title="Refresh availability">
            <span>
            <IconButton
              aria-label="Refresh"
              onClick={() => loadDailyAvailability(selectedDate)}
              disabled={loading || refreshing || generating || !selectedKitchenId}
              sx={{ width: 40, height: 40, color: "text.secondary", border: "1px solid", borderColor: "divider", borderRadius: 2 }}
            >
              {refreshing ? <CircularProgress size={18} aria-label="Refreshing availability" /> : <RefreshIcon fontSize="small" />}
            </IconButton>
            </span>
            </Tooltip>

            <Button
              variant="contained"
              color={isProjected ? "secondary" : "primary"}
              size="small"
              onClick={() => handleGenerate(false)}
              disabled={generating || loading || !selectedKitchenId}
              startIcon={generating ? <CircularProgress size={16} /> : <BoltIcon />}
              sx={{ textTransform: "none", fontWeight: 600, gridColumn: "1 / -1", px: 2 }}
            >
              {isProjected ? "Lock In / Generate for Date" : "Recalculate Availability"}
            </Button>

            <Button
              variant="outlined"
              color="inherit"
              size="small"
              startIcon={<AddIcon />}
              onClick={() => setAddMealModalOpen(true)}
              disabled={loading || generating || !selectedKitchenId}
              sx={{ textTransform: "none", fontWeight: 600, borderColor: "divider", gridColumn: "1 / -1", px: 2 }}
            >
              Add Extra Meal
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {/* Projection Notice Banner */}
      {isProjected && (
        <Alert
          severity="info"
          sx={{ borderRadius: 2 }}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => handleGenerate(false)}
              sx={{ fontWeight: 600 }}
            >
              Lock In Now
            </Button>
          }
        >
          You are previewing <strong>projected availability</strong> for {selectedDate} derived from active recurring rules. Click <strong>Lock In / Generate</strong> to lock this day in and customize prep quantities.
        </Alert>
      )}

      {/* Existing summary values, presented in one compact panel. */}
      <Paper elevation={0} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 3, overflow: "hidden" }}>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", md: "repeat(4, minmax(0, 1fr))" },
          "& > div": { px: { xs: 2, sm: 3 }, py: 2, minWidth: 0 },
          "& > div + div": { borderLeft: "1px solid", borderColor: "divider" },
          "& > div:nth-of-type(3)": { borderLeft: { xs: 0, md: "1px solid" }, borderColor: "divider" },
          "& > div:nth-of-type(n+3)": { borderTop: { xs: "1px solid", md: 0 }, borderColor: "divider" },
        }}>
          <Box>
            <Typography variant="body2" color="text.secondary">Available meals</Typography>
            <Typography sx={{ mt: 0.75, fontSize: 28, fontWeight: 750, lineHeight: 1.2, fontVariantNumeric: "tabular-nums" }}>
              {liveMealsCount}<Box component="span" sx={{ ml: 1, fontSize: 16, fontWeight: 500, color: "text.secondary" }}>/ {totalMealRecords.length}</Box>
            </Typography>
          </Box>
          <Box>
            <Tooltip title="Configured limited meal stock before sales. Unlimited meals are excluded; shared variants may be counted separately.">
              <Typography variant="body2" color="text.secondary" sx={{ cursor: "help" }}>Limited stock capacity</Typography>
            </Tooltip>
            <Typography sx={{ mt: 0.75, fontSize: 28, fontWeight: 750, lineHeight: 1.2, fontVariantNumeric: "tabular-nums" }}>
              {data?.summary?.totalSellableUnits || 0}<Box component="span" sx={{ ml: 1, fontSize: 14, fontWeight: 500, color: "text.secondary" }}>{data?.summary?.totalSellableUnits === 1 ? "unit" : "units"}</Box>
            </Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">Sold so far</Typography>
            <Typography sx={{ mt: 0.75, fontSize: 28, fontWeight: 750, lineHeight: 1.2, fontVariantNumeric: "tabular-nums" }}>
              {data?.summary?.soldUnits || 0}<Box component="span" sx={{ ml: 1, fontSize: 14, fontWeight: 500, color: "text.secondary" }}>{data?.summary?.soldUnits === 1 ? "unit" : "units"}</Box>
            </Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">Unavailable meals</Typography>
            <Typography sx={{ mt: 0.75, fontSize: 28, fontWeight: 750, lineHeight: 1.2, fontVariantNumeric: "tabular-nums", color: unavailableMealsCount ? "error.main" : "text.primary" }}>{unavailableMealsCount}</Typography>
          </Box>
        </Box>
        <Stack direction="row" sx={{ px: { xs: 2, sm: 3 }, py: 1, gap: 2, flexWrap: "wrap", alignItems: "center", bgcolor: "#fafbfc", borderTop: "1px solid", borderColor: "divider" }}>
          <Typography variant="caption" color="text.secondary">
            Active combos: {data?.summary?.activeAvailableCombos ?? (data?.combos?.filter((c) => c.isAvailable && c.isConstituentAvailable !== false).length || 0)} / {data?.summary?.totalCombos ?? (data?.combos?.length || 0)}
          </Typography>
          <Chip size="small" variant="outlined" label={`${data?.summary?.manualOverrideCount || 0} daily overrides`} sx={{ height: 24, fontSize: 12, borderColor: "divider", color: (data?.summary?.manualOverrideCount || 0) > 0 ? "warning.dark" : "text.secondary" }} />
        </Stack>
      </Paper>

      {/* Catalog SubTabs Switcher */}
      <Paper elevation={0} sx={{ borderRadius: 2.5, border: "1px solid", borderColor: "divider", px: { xs: 0, sm: 1 }, minWidth: 0, bgcolor: "#fafbfc" }}>
        <Tabs
          value={activeSubTab}
          onChange={(_, val) => setActiveSubTab(val)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            "& .MuiTabs-indicator": { height: 3, borderRadius: "3px 3px 0 0" },
            "& .MuiTab-root": { textTransform: "none", fontWeight: 600, fontSize: "0.95rem", py: 1.5, maxWidth: { xs: 220, sm: 360 }, whiteSpace: "normal", color: "text.secondary",
              "&.Mui-selected": { bgcolor: "background.paper", color: "primary.main" },
              "& .MuiTab-iconWrapper": { flexShrink: 0 } },
          }}
        >
          <Tab
            value="meals"
            icon={<RestaurantMenuIcon fontSize="small" />}
            iconPosition="start"
            label={`Main Dishes & Add-ons (${totalMealRecords.length})`}
          />
          <Tab
            value="combos"
            icon={<FastfoodIcon fontSize="small" />}
            iconPosition="start"
            label={`Meal Combos (${totalComboRecords.length})`}
          />
          <Tab
            value="sides"
            icon={<LocalDrinkIcon fontSize="small" />}
            iconPosition="start"
            label={`Menu Sides & Drinks · all kitchens (${(data?.menuSides || []).length})`}
          />
        </Tabs>
      </Paper>

      {/* SECTION 1: MAIN DISHES & ADD-ONS */}
      {activeSubTab === "meals" && (
        <>
          <DailySharedPools records={totalMealRecords} groups={data?.sharedStockGroups} onEdit={handleOpenQtyModal} />
          {/* Filters & Search */}
          <Paper elevation={0} sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 2.5, border: "1px solid", borderColor: "divider" }}>
            <Stack direction={{ xs: "column", lg: "row" }} spacing={2} alignItems={{ xs: "stretch", lg: "center" }}>
              <ToggleButtonGroup value={mealView} exclusive size="small" aria-label="Meal view" sx={{ alignSelf: "flex-start", bgcolor: "#fafbfc", "& .MuiToggleButton-root": { minHeight: 40, borderColor: "divider" }, "& .Mui-selected": { color: "text.primary", bgcolor: "#eef0f3" } }} onChange={(_, view) => view && setMealView(view)}>
                <Tooltip title="Card view"><ToggleButton value="cards" aria-label="Card view"><ViewModuleIcon /></ToggleButton></Tooltip>
                <Tooltip title="List view"><ToggleButton value="list" aria-label="List view"><ViewListIcon /></ToggleButton></Tooltip>
              </ToggleButtonGroup>
              <TextField
                size="small"
                placeholder="Search dish name..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{ flex: 1, minWidth: 0, width: { xs: "100%", lg: "auto" }, "& .MuiOutlinedInput-root": { bgcolor: "#fafbfc" } }}
              />

              <FormControl size="small" sx={{ minWidth: { xs: 0, lg: 180 }, width: { xs: "100%", lg: "auto" } }}>
                <InputLabel>Category</InputLabel>
                <Select
                  label="Category"
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                >
                  <MenuItem value="">All Categories</MenuItem>
                  {categories.map((c) => (
                    <MenuItem key={c._id} value={c._id}>
                      {c.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <ToggleButtonGroup
                value={statusFilter}
                exclusive
                onChange={(_, val) => val && setStatusFilter(val)}
                size="small"
                sx={{ minHeight: 40, display: { xs: "grid", sm: "flex" }, gridTemplateColumns: "repeat(3, minmax(0, 1fr))", bgcolor: "#fafbfc",
                  "& .MuiToggleButton-root": { minWidth: 0, px: { xs: 0.5, sm: 2 }, whiteSpace: "normal", lineHeight: 1.35, borderColor: "divider" },
                  "& .MuiToggleButton-root.Mui-selected": { bgcolor: "#eef0f3" } }}
              >
                <ToggleButton value="ALL" sx={{ px: 2, fontSize: 13, textTransform: "none", fontWeight: 600 }}>
                  All ({totalMealRecords.length})
                </ToggleButton>
                <ToggleButton
                  value="LIVE"
                  sx={{
                    px: 2,
                    fontSize: 13,
                    textTransform: "none",
                    fontWeight: 600,
                    color: statusFilter === "LIVE" ? "success.dark" : "success.main",
                  }}
                >
                  In Stock ({liveMealsCount})
                </ToggleButton>
                <ToggleButton value="UNAVAILABLE" sx={{ px: 2, fontSize: 13, textTransform: "none", fontWeight: 600 }}>
                  Out of Stock ({unavailableMealsCount})
                </ToggleButton>
              </ToggleButtonGroup>
            </Stack>
          </Paper>

          {/* Meals Table */}
          {mealView === "cards" ? (loading ? <CircularProgress /> : filteredRecords.length ?
            <DailyMealCards key={`${selectedDate}:${selectedKitchenId}`} records={filteredRecords} categories={categories} onEdit={handleOpenQtyModal} onToggle={handleToggle} /> :
            <Typography sx={{ py: 4, textAlign: "center" }}>No scheduled meals match these filters.</Typography>) : <TableContainer
            component={Paper}
            elevation={0}
            sx={{ borderRadius: 3, border: "1px solid", borderColor: "divider", maxWidth: "100%", overflowX: "auto" }}
          >
            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", p: 8 }}>
                <CircularProgress />
              </Box>
            ) : filteredRecords.length === 0 ? (
              <Box sx={{ p: 6, textAlign: "center" }}>
                <InventoryIcon sx={{ fontSize: 48, color: "text.secondary", mb: 1 }} />
                <Typography variant="h6" color="text.secondary">
                  No dishes found for this date
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Try adjusting your search filters or click "Lock In / Generate" to evaluate availability rules.
                </Typography>
              </Box>
            ) : (
              <Table sx={{ minWidth: 1000 }}>
                <TableHead sx={{ bgcolor: "action.hover" }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Meal</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Availability Source</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Stock Mode</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700 }}>
                      Planned Capacity
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700 }}>
                      Sold
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700 }}>
                      Remaining Sellable
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700 }}>
                      Daily Status
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredRecords.map((record) => {
                    const dailyStatus = getDailyMealStatus(record);
                    const isOutOfStock =
                      record.stockMode === "LIMITED" && (record.remainingQuantity || 0) <= 0;

                    return (
                      <TableRow
                        key={record._id}
                        hover
                        sx={{
                          bgcolor: !record.isAvailable
                            ? "action.hover"
                            : record.isManuallyOverridden
                            ? "warning.lighter"
                            : "inherit",
                        }}
                      >
                        {/* Meal Info */}
                        <TableCell>
                          <Stack direction="row" spacing={2} alignItems="center">
                            <Avatar
                              src={record.mealId?.image}
                              variant="rounded"
                              sx={{ width: 44, height: 44 }}
                            />
                            <Box>
                              <Typography variant="subtitle2" fontWeight={600}>
                                {record.mealId?.name}
                              </Typography>
                              <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.2 }}>
                                <Typography variant="caption" color="text.secondary">
                                  ₹{record.mealId?.currentPrice}
                                </Typography>
                                {record.mealId?.isAddOn && (
                                  <Chip label="Add-on" size="small" sx={{ height: 18, fontSize: 10 }} />
                                )}
                              </Stack>
                            </Box>
                          </Stack>
                        </TableCell>

                        {/* Availability Source */}
                        <TableCell>
                          {record.source === "RULE" && record.appliedRuleId ? (
                            <Tooltip title={`Applied rule: ${record.appliedRuleId.ruleName || "Rule"}`}>
                              <Chip
                                label={record.appliedRuleId.ruleName || "Rule Defined"}
                                size="small"
                                color="primary"
                                variant="outlined"
                                sx={{ fontWeight: 500 }}
                              />
                            </Tooltip>
                          ) : record.source === "SWAP_SET" ? (
                            <Chip label={`Swap set ${record.swapSetSerial || ""}`} size="small" color="primary" variant="outlined" />
                          ) : record.source === "MANUAL_ADDITION" ? (
                            <Chip
                              label="Manually Added"
                              size="small"
                              color="secondary"
                              variant="outlined"
                              sx={{ fontWeight: 500 }}
                            />
                          ) : (
                            <Typography variant="caption" color="text.secondary">
                              No Active Rule
                            </Typography>
                          )}
                        </TableCell>

                        {/* Stock Mode */}
                        <TableCell>
                          <Chip
                            label={record.stockMode === "UNLIMITED" ? "Unlimited" : "Limited Stock"}
                            size="small"
                            variant="outlined"
                            sx={{ fontWeight: 500 }}
                          />
                        </TableCell>

                        {/* Planned Capacity */}
                        <TableCell align="center">
                          <Typography variant="body2" fontWeight={600}>
                            {record.stockMode === "UNLIMITED" ? "—" : record.finalConfiguredQuantity}
                          </Typography>
                          {record.isManuallyOverridden && record.ruleDerivedQuantity !== record.finalConfiguredQuantity && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              Rule: {record.ruleDerivedQuantity}
                            </Typography>
                          )}
                        </TableCell>

                        {/* Sold */}
                        <TableCell align="center">
                          <Typography variant="body2" fontWeight={600} color="success.main">
                            {record.soldQuantity || 0}
                          </Typography>
                        </TableCell>

                        {/* Remaining */}
                        <TableCell align="center">
                          {record.stockMode === "UNLIMITED" ? (
                            <Typography variant="caption" color="text.secondary">
                              Unlimited
                            </Typography>
                          ) : dailyStatus === "OFF_SCHEDULE" ? (
                            <Chip
                              label="Off Schedule"
                              size="small"
                              variant="outlined"
                              sx={{ color: "text.secondary", borderColor: "divider", fontWeight: 500 }}
                            />
                          ) : isOutOfStock ? (
                            <Chip
                              label="Sold Out"
                              size="small"
                              color="error"
                              sx={{ fontWeight: 700 }}
                            />
                          ) : (
                            <Chip
                              label={`${record.remainingQuantity} left`}
                              size="small"
                              color={(record.remainingQuantity || 0) < 5 ? "warning" : "success"}
                              variant="outlined"
                              sx={{ fontWeight: 600 }}
                            />
                          )}
                        </TableCell>

                        {/* Status */}
                        <TableCell align="center">
                          <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
                            <Switch
                              size="small"
                              checked={Boolean(record.isAvailable)}
                              onChange={() => handleToggle(record)}
                              color="success"
                              disabled={record.isProjectedOnly}
                            />
                            <Typography
                              variant="caption"
                              fontWeight={600}
                              color={
                                record.isAvailable
                                  ? "success.main"
                                  : dailyStatus === "OFF_SCHEDULE"
                                  ? "text.secondary"
                                  : "error.main"
                              }
                            >
                              {dailyStatus === "LIVE" ? "In Stock" : dailyStatus === "PAUSED" ? "Out of Stock (Paused)" : "Out of Stock"}
                            </Typography>
                          </Stack>
                        </TableCell>

                        {/* Actions */}
                        <TableCell align="right">
                          <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                            <Tooltip title="Edit Capacity">
                              <IconButton
                                size="small"
                                onClick={() => handleOpenQtyModal(record)}
                                color="primary"
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>

                            {record.isManuallyOverridden && !record.isProjectedOnly && (
                              <Tooltip title="Revert to Rule Default">
                                <IconButton
                                  size="small"
                                  onClick={() => handleRevert(record)}
                                  color="warning"
                                >
                                  <UndoIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </TableContainer>}
        </>
      )}

      {/* SECTION 2: MEAL COMBOS */}
      {activeSubTab === "combos" && (
        <>
          <Paper elevation={0} sx={{ p: 2, borderRadius: 2.5, border: "1px solid", borderColor: "divider" }}>
            <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ xs: "stretch", md: "center" }} justifyContent="space-between">
              <Typography variant="subtitle2" color="text.secondary">
                Filter combo status for {selectedDate}:
              </Typography>
              <ToggleButtonGroup
                value={comboStatusFilter}
                exclusive
                onChange={(_, val) => val && setComboStatusFilter(val)}
                size="small"
                sx={{ minHeight: 44, display: { xs: "grid", sm: "flex" }, gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
                  "& .MuiToggleButton-root": { minWidth: 0, px: { xs: 0.5, sm: 2 }, whiteSpace: "normal", lineHeight: 1.35 } }}
              >
                <ToggleButton value="ALL" sx={{ px: 2, fontSize: 13, textTransform: "none", fontWeight: 600 }}>
                  All Combos ({totalComboRecords.length})
                </ToggleButton>
                <ToggleButton
                  value="LIVE"
                  sx={{
                    px: 2,
                    fontSize: 13,
                    textTransform: "none",
                    fontWeight: 600,
                    color: comboStatusFilter === "LIVE" ? "success.dark" : "success.main",
                  }}
                >
                  Live on Menu ({liveCombosCount})
                </ToggleButton>
                <ToggleButton
                  value="OFF_SCHEDULE"
                  sx={{
                    px: 2,
                    fontSize: 13,
                    textTransform: "none",
                    fontWeight: 600,
                    color: "text.secondary",
                  }}
                >
                  Off Schedule ({offScheduleCombosCount})
                </ToggleButton>
              </ToggleButtonGroup>
            </Stack>
          </Paper>

          <TableContainer
            component={Paper}
            elevation={0}
            sx={{ borderRadius: 3, border: "1px solid", borderColor: "divider" }}
          >
            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", p: 8 }}>
                <CircularProgress />
              </Box>
            ) : filteredCombos.length === 0 ? (
              <Box sx={{ p: 6, textAlign: "center" }}>
                <FastfoodIcon sx={{ fontSize: 48, color: "text.secondary", mb: 1 }} />
                <Typography variant="h6" color="text.secondary">
                  No meal combos match this filter
                </Typography>
              </Box>
            ) : (
              <Table>
                <TableHead sx={{ bgcolor: "action.hover" }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Meal Combo</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Pricing</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Constituent Dishes & Readiness</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700 }}>
                      Derived Capacity
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700 }}>
                      Constituent Health
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700 }}>
                      Menu Status
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>
                      Daily Toggle
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredCombos.map((combo) => {
                  const isBlocked = combo.isConstituentAvailable === false;
                  return (
                    <TableRow
                      key={combo._id}
                      hover
                      sx={{
                        bgcolor: isBlocked
                          ? "rgba(239, 68, 68, 0.04)"
                          : !combo.isAvailable
                          ? "action.hover"
                          : combo.isManuallyOverridden
                          ? "warning.lighter"
                          : "inherit",
                      }}
                    >
                      <TableCell>
                        <Stack direction="row" spacing={2} alignItems="center">
                          <Avatar
                            src={combo.mealId?.image}
                            variant="rounded"
                            sx={{ width: 44, height: 44 }}
                          >
                            <FastfoodIcon />
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle2" fontWeight={600}>
                              {combo.mealId?.title}
                            </Typography>
                            {combo.mealId?.description && (
                              <Typography variant="caption" color="text.secondary" sx={{ display: "block", maxWidth: 260 }} noWrap>
                                {combo.mealId?.description}
                              </Typography>
                            )}
                          </Box>
                        </Stack>
                      </TableCell>

                      <TableCell>
                        <Typography variant="subtitle2" fontWeight={700} color="success.main">
                          ₹{combo.mealId?.comboPrice}
                        </Typography>
                        {combo.mealId?.originalPrice && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ textDecoration: "line-through" }}
                          >
                            ₹{combo.mealId?.originalPrice}
                          </Typography>
                        )}
                      </TableCell>

                      <TableCell sx={{ maxWidth: 360 }}>
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                          {(combo.mealId?.items || []).map((item, idx) => {
                            const subMeal = item.instantMealId;
                            const isMissing = (combo.unavailableConstituents || []).some(
                              (u) => String(u.mealId) === String(subMeal?._id)
                            );
                            return (
                              <Chip
                                key={idx}
                                label={`${subMeal?.name || "Dish"} ×${item.quantity}`}
                                size="small"
                                color={isMissing ? "error" : "default"}
                                variant={isMissing ? "filled" : "outlined"}
                                icon={isMissing ? <WarningAmberIcon /> : undefined}
                                sx={{ fontWeight: 500, fontSize: "0.75rem" }}
                              />
                            );
                          })}
                        </Stack>
                        {combo.unavailableConstituents && combo.unavailableConstituents.length > 0 && (
                          <Typography variant="caption" color="error.main" sx={{ display: "block", mt: 0.5, fontWeight: 500 }}>
                            Missing / Out of stock: {combo.unavailableConstituents.map((u) => u.name || "Dish").join(", ")}
                          </Typography>
                        )}
                      </TableCell>

                      <TableCell align="center">
                        <Typography variant="body2" fontWeight={700}>
                          {combo.calculatedConstituentCapacity !== null && combo.calculatedConstituentCapacity !== undefined
                            ? `${combo.calculatedConstituentCapacity} sets max`
                            : "—"}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          from dish stock
                        </Typography>
                      </TableCell>

                      <TableCell align="center">
                        {isBlocked ? (
                          <Chip
                            label="Dish Sold Out"
                            size="small"
                            color="error"
                            icon={<WarningAmberIcon />}
                            sx={{ fontWeight: 600 }}
                          />
                        ) : (
                          <Chip
                            label="Constituents Ready"
                            size="small"
                            color="success"
                            icon={<CheckCircleIcon />}
                            sx={{ fontWeight: 600 }}
                          />
                        )}
                      </TableCell>

                      <TableCell align="center">
                        {combo.isAvailable && !isBlocked ? (
                          <Chip label="LIVE ON MENU" size="small" color="success" sx={{ fontWeight: 700 }} />
                        ) : isBlocked ? (
                          <Chip label="AUTO-PAUSED" size="small" color="error" sx={{ fontWeight: 700 }} />
                        ) : (
                          <Chip label="OFF SCHEDULE" size="small" variant="outlined" sx={{ fontWeight: 600 }} />
                        )}
                      </TableCell>

                      <TableCell align="right">
                        <Tooltip
                          title={
                            isBlocked
                              ? "Cannot enable combo: one or more constituent dishes are unavailable or depleted today."
                              : combo.isAvailable
                              ? "Click to pause combo for today"
                              : "Click to enable combo for today"
                          }
                        >
                          <span>
                            <Switch
                              checked={combo.isAvailable && !isBlocked}
                              disabled={isBlocked || combo.isProjectedOnly}
                              onChange={() => handleToggleCombo(combo)}
                              color="success"
                            />
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </TableContainer>
      </>
    )}

      {/* SECTION 3: MENU SIDES & DRINKS */}
      {activeSubTab === "sides" && (
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{ borderRadius: 3, border: "1px solid", borderColor: "divider" }}
        >
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 8 }}>
              <CircularProgress />
            </Box>
          ) : (data?.menuSides || []).length === 0 ? (
            <Box sx={{ p: 6, textAlign: "center" }}>
              <LocalDrinkIcon sx={{ fontSize: 48, color: "text.secondary", mb: 1 }} />
              <Typography variant="h6" color="text.secondary">
                No menu sides configured
              </Typography>
            </Box>
          ) : (
            <Table>
              <TableHead sx={{ bgcolor: "action.hover" }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Item Name</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Category</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Price</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700 }}>
                    Availability
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>
                    Quick Toggle
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(data?.menuSides || []).map((side) => (
                  <TableRow key={side._id} hover>
                    <TableCell>
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <Avatar sx={{ width: 36, height: 36, bgcolor: "action.selected" }}>
                          <LocalDrinkIcon fontSize="small" />
                        </Avatar>
                        <Typography variant="subtitle2" fontWeight={600}>
                          {side.name}
                        </Typography>
                      </Stack>
                    </TableCell>

                    <TableCell>
                      <Chip
                        label={side.category.toUpperCase()}
                        size="small"
                        variant="outlined"
                        sx={{ fontWeight: 600, fontSize: 11 }}
                      />
                    </TableCell>

                    <TableCell>
                      <Typography variant="subtitle2" fontWeight={600}>
                        ₹{side.price}
                      </Typography>
                    </TableCell>

                    <TableCell align="center">
                      <Chip
                        label={side.available ? "AVAILABLE" : "UNAVAILABLE"}
                        color={side.available ? "success" : "default"}
                        size="small"
                        sx={{ fontWeight: 700 }}
                      />
                    </TableCell>

                    <TableCell align="right">
                      <Switch
                        checked={Boolean(side.available)}
                        disabled={!canEditSides || savingSide}
                        inputProps={{ "aria-label": `${side.name} · all kitchens` }}
                        onChange={() => handleToggleSide(side)}
                        color="success"
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </TableContainer>
      )}

      {/* Edit Quantity Modal */}
      <InstantDayPlanDialog open={planOpen} date={selectedDate} kitchenId={selectedKitchenId}
        kitchenName={kitchens.find(kitchen => kitchen._id === selectedKitchenId)?.name || ""}
        onClose={() => setPlanOpen(false)} onUpdated={async () => { await loadDailyAvailability(selectedDate); }} />

      <Dialog open={qtyModalOpen} onClose={() => { if (!savingQuantity) setQtyModalOpen(false); }} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>{selectedRecord?.sharedStockGroupId ? "Adjust Daily Pool Stock" : "Adjust Remaining Stock"}</DialogTitle>
        <DialogContent dividers>
          <Typography variant="subtitle2" gutterBottom>
            {selectedRecord?.sharedStockGroupId ? data?.sharedStockGroups?.find(group => group._id === selectedRecord.sharedStockGroupId)?.name || "Shared pool" : selectedRecord?.mealId?.name}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
            Operating Date: {selectedDate} | Units Sold: {selectedRecord?.soldQuantity || 0}
          </Typography>

          {selectedRecord?.sharedStockGroupId && <Alert severity="info" sx={{ mb: 2 }}>Remaining stock changes apply to scheduled meals in this shared stock group.</Alert>}
          <ToggleButtonGroup value={editStockMode} exclusive onChange={(_, value) => value && setEditStockMode(value)} disabled={savingQuantity} size="small" sx={{ mb: 2 }}>
            <ToggleButton value="LIMITED">Limited</ToggleButton><ToggleButton value="UNLIMITED">Unlimited</ToggleButton>
          </ToggleButtonGroup>
          {editStockMode === "LIMITED" && <TextField
            label="Portions remaining"
            helperText="Set how many portions are available now. Units already sold are preserved."
            type="number"
            fullWidth
            value={newQuantity}
            onChange={(e) => setNewQuantity(e.target.value)}
            sx={{ mb: 2 }}
            disabled={savingQuantity}
            inputProps={{ min: 0, step: 1 }}
          />}

          <TextField
            label="Override Reason (Optional)"
            placeholder="e.g. Extra prep batch for evening catering"
            fullWidth
            multiline
            rows={2}
            value={overrideReason}
            onChange={(e) => setOverrideReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setQtyModalOpen(false)} color="inherit" disabled={savingQuantity}>
            Cancel
          </Button>
          <Button onClick={handleSaveQuantity} variant="contained" color="primary" disabled={savingQuantity}>
            Save Adjustment
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Extra Meal Modal */}
      <Dialog open={addMealModalOpen} onClose={() => setAddMealModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Add Extra Meal for {selectedDate}</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Add an unscheduled dish to this specific operating date. This will not alter recurring rules.
          </Typography>

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Select Instant Meal</InputLabel>
            <Select
              label="Select Instant Meal"
              value={addMealId}
              onChange={(e) => setAddMealId(e.target.value)}
            >
              {allCatalogMeals.map((meal) => (
                <MenuItem key={meal._id} value={meal._id}>
                  {meal.name} (₹{meal.currentPrice})
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Stock Mode</InputLabel>
            <Select
              label="Stock Mode"
              value={addMealStockMode}
              onChange={(e) => setAddMealStockMode(e.target.value as "LIMITED" | "UNLIMITED")}
            >
              <MenuItem value="LIMITED">Limited Stock (Fixed Units)</MenuItem>
              <MenuItem value="UNLIMITED">Unlimited / Untracked Stock</MenuItem>
            </Select>
          </FormControl>

          {addMealStockMode === "LIMITED" && (
            <TextField
              label="Sellable Quantity"
              type="number"
              fullWidth
              value={addMealQty}
              onChange={(e) => setAddMealQty(Number(e.target.value))}
              sx={{ mb: 2 }}
            />
          )}

          <TextField
            label="Reason for Addition (Optional)"
            placeholder="e.g. Chef special / seasonal launch"
            fullWidth
            multiline
            rows={2}
            value={addMealReason}
            onChange={(e) => setAddMealReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setAddMealModalOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button onClick={handleAddManualMeal} variant="contained" color="primary">
            Add to Menu
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DailyAvailabilityTab;
